import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3067/api';

export interface DateRange {
  startDate?: string; // MM/DD/YYYY
  endDate?: string;   // MM/DD/YYYY
}

class SnowflakeService {
  private apiClient;

  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for authentication
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async executeQuery(query: string) {
    try {
      const response = await this.apiClient.post('/snowflake/query', { query });
      return response.data;
    } catch (error) {
      console.error('Error executing Snowflake query:', error);
      throw error;
    }
  }

  async getMetrics(range?: DateRange) {
    try {
      const response = await this.apiClient.get('/snowflake/metrics', {
        params: {
          startDate: range?.startDate,
          endDate: range?.endDate,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      // Return mock data for development
      return this.getMockMetrics();
    }
  }

  async getChartData(range?: DateRange) {
    try {
      const response = await this.apiClient.get('/snowflake/chart-data', {
        params: {
          startDate: range?.startDate,
          endDate: range?.endDate,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chart data:', error);
      // Return mock data for development
      return this.getMockChartData();
    }
  }

  async getTableData(tableName: string, limit: number = 100) {
    try {
      const response = await this.apiClient.get(`/snowflake/table/${tableName}`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching table data:', error);
      throw error;
    }
  }

  async getDatabases() {
    try {
      const response = await this.apiClient.get('/snowflake/databases');
      return response.data;
    } catch (error) {
      console.error('Error fetching databases:', error);
      return [];
    }
  }

  async getSchemas(database: string) {
    try {
      const response = await this.apiClient.get(`/snowflake/databases/${database}/schemas`);
      return response.data;
    } catch (error) {
      console.error('Error fetching schemas:', error);
      return [];
    }
  }

  async getTables(database: string, schema: string) {
    try {
      const response = await this.apiClient.get(`/snowflake/databases/${database}/schemas/${schema}/tables`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tables:', error);
      return [];
    }
  }

  // Mock data for development/testing
  private getMockMetrics() {
    return [
      { id: 1, title: 'Total Records', value: '1,234,567', change: '+12%', trend: 'up' },
      { id: 2, title: 'Active Queries', value: '45', change: '-3%', trend: 'down' },
      { id: 3, title: 'Data Storage', value: '2.4 TB', change: '+8%', trend: 'up' },
      { id: 4, title: 'Query Performance', value: '1.2s', change: '-15%', trend: 'up' },
    ];
  }

  private getMockChartData() {
    return [
      { name: 'Jan', queries: 4000, storage: 2400, performance: 2400 },
      { name: 'Feb', queries: 3000, storage: 1398, performance: 2210 },
      { name: 'Mar', queries: 2000, storage: 9800, performance: 2290 },
      { name: 'Apr', queries: 2780, storage: 3908, performance: 2000 },
      { name: 'May', queries: 1890, storage: 4800, performance: 2181 },
      { name: 'Jun', queries: 2390, storage: 3800, performance: 2500 },
    ];
  }
}

export const snowflakeService = new SnowflakeService();
